/**
 * RFC 9457 problem details — the shape every Catalog route answers with, on 4xx and 5xx alike,
 * so one parser reads both (data-model §5).
 *
 * Hand-typed rather than generated because it is the *error* channel: openapi-fetch surfaces it
 * as `unknown`, and this is the narrowing. It carries no business field.
 */
export type ProblemDetails = {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  [member: string]: unknown;
};

export function isProblemDetails(value: unknown): value is ProblemDetails {
  if (typeof value !== "object" || value === null) return false;
  const v = value as Record<string, unknown>;
  return typeof v.title === "string" || typeof v.detail === "string" || typeof v.status === "number";
}

/** The service's one-line reason ("Code already in use") for a toast, else its detail, else `fallback`. */
export function problemTitle(error: unknown, fallback: string): string {
  if (isProblemDetails(error)) return error.title ?? error.detail ?? fallback;
  return fallback;
}

/**
 * A generated client's `error` channel, widened so it can be read and reported.
 *
 * openapi-fetch types `error` as **`never`** for any operation whose document declares no error
 * response body, and several services still declare none (backend-gaps GAP-4). `never` does not
 * mean "this call cannot fail" — it means the document did not say what a failure looks like; the
 * service still answers 4xx and 5xx with a problem document. The practical consequence is that
 * `if (r.error) render(r.error)` does not compile: the truthiness test narrows the whole result
 * to `never`, so the second read has nothing to read.
 *
 * Passing the value through here once restores `unknown`, which is what `ErrorSurface` and
 * `isProblemDetails` already accept. It is deliberately **not** an assertion: nothing here claims
 * the body is a problem document, so a service that answers with something else still renders
 * whatever it actually sent (constitution VI) instead of being narrowed away or cast into a shape
 * it does not have.
 */
export function failureOf(error: unknown): unknown {
  return error;
}
